"use client";

import * as styles from "./styles.css";
import PageSection from "@/components/page-section";
import ContentContainer from "@/components/content-container";
import { stegaClean } from "@sanity/client/stega";
import Image from "next/image";
import Button from "@/components/button";
import { CharacterModule } from "@/types/sanity.types";
import { PortableText } from "next-sanity";
import { ptComponents } from "@/utils/portableTextComp";
import Link from "next/link";

// Extend the type to include rollerID temporarily until types are regenerated
type ExtendedCharacterModule = CharacterModule & {
  rollerID?: string;
  linkType?: "internal link" | "external link" | "roller ID";
};

export default function Character(props: CharacterModule) {
  const extendedProps = props as ExtendedCharacterModule;
  const linkType = extendedProps.linkType as "internal link" | "external link" | "roller ID" | undefined;
  const isRollerID = linkType === "roller ID";
  const isExternalLink = linkType === "external link";
  const isInternalLink = linkType === "internal link";

  // Determine the href based on link type
  const getHref = () => {
    if (isExternalLink) return extendedProps?.buttonExternalLink ?? "/";
    if (isInternalLink) return extendedProps?.buttonInternalLink?.buttonLink ?? "/";
    return "/"; // Default for roller ID or undefined
  };

  const hasButton = extendedProps?.buttonExternalLink || extendedProps?.buttonInternalLink?.buttonLink || extendedProps?.rollerID;

  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    // If it's a roller ID, use a div with onClick handler
    if (extendedProps.buttonVisible && isRollerID) {
      return (
        <div className={`${styles.link}${!hasButton ? " no--button" : ""}`}>
          {children}
        </div>
      );
    }
    
    // If it's a regular link, use Link component
    if (extendedProps.buttonVisible && (isExternalLink || isInternalLink)) {
      return (
        <Link
          href={getHref()}
          target={isExternalLink ? "_blank" : "_self"}
          className={`${styles.link}${!hasButton ? " no--button" : ""}`}
          prefetch={false}
        >
          {children}
        </Link>
      );
    }

    // If button is not visible, use a div
    return <div className={styles.link}>{children}</div>;
  };

  return (
    <PageSection
      backgroundColor={
        stegaClean(extendedProps?.characterColor) ?? "background-default"
      }
    >
      <ContentContainer layout="character">
        <div
          className="hidden-anchor"
          id={stegaClean(extendedProps?.headingText)
            ?.toLowerCase()
            .replace(/[^A-Z0-9]/gi, "-")}
        />
        <div
          className={styles.container({
            backgroundColor:
              stegaClean(extendedProps?.characterColor) ?? "background-default",
          })}
        >
          <ContentWrapper>
            {extendedProps.character && extendedProps.character?.imageSource && (
              <div className={styles.imageContainer}>
                <Image
                  src={`${extendedProps.character.imageSource.url}?h=256&q=90&auto=format`}
                  alt={extendedProps.character.imageSource.imageAlt ?? ""}
                  fill={true}
                  style={{ objectFit: "cover" }}
                  placeholder="blur"
                  blurDataURL={`${extendedProps.character.imageSource?.lqip ?? `${extendedProps.character.imageSource.url}?w=10&auto=format`}`}
                />
              </div>
            )}
            <div
              className={`${styles.textContainer}${hasButton ? " has--button" : " no--button"}`}
            >
              {extendedProps.headingText && <h3>{extendedProps.headingText}</h3>}
              {extendedProps.bodyText && (
                <div className="portable-text">
                  <PortableText
                    value={extendedProps.bodyText}
                    components={ptComponents}
                  />
                </div>
              )}
            </div>
            {extendedProps.buttonVisible && hasButton && (
              <div className={styles.buttonContainer}>
                <Button
                  style="primaryCircular"
                  size="large"
                  circular="right"
                  href={!isRollerID ? getHref() : undefined}
                  linkType={extendedProps.linkType}
                  rollerProductID={isRollerID ? extendedProps.rollerID : undefined}
                  target={isExternalLink ? "_blank" : ""}
                />
                <Button
                  style="primaryCircular"
                  size="small"
                  circular="right"
                  href={!isRollerID ? getHref() : undefined}
                  linkType={extendedProps.linkType}
                  rollerProductID={isRollerID ? extendedProps.rollerID : undefined}
                  target={isExternalLink ? "_blank" : ""}
                />
              </div>
            )}
          </ContentWrapper>
        </div>
      </ContentContainer>
    </PageSection>
  );
}